import { copilotUtils } from 'utils/copilot';
import { unsupportedAttachmentReason } from 'utils/mimDraft';

import {
  type CopilotActionDefinition,
  type CopilotActionId,
} from './copilotActions.types';

/**
 * The canonical definition of every pinnable copilot action. Editor contexts
 * pick from these and may override `name`; nothing else is per-context.
 */
export const copilotActionRegistry: Record<
  CopilotActionId,
  CopilotActionDefinition
> = {
  improveReply: {
    id: 'improveReply',
    icon: 'pen',
    name: 'Improve reply',
    shortName: 'Improve',
    commandName: 'richTextEditor:improveReply',
    // promptName comes from the improvement the user picks.
    execution: { kind: 'writingImprovement' },
    insert: 'replace',
    defaultPinned: false,
    loadingText: 'Improving reply',
    errorText: 'Failed to improve reply',
    getStatus: ({ text, isLoading, name }) =>
      copilotUtils.improveReply.getStatus({ text, isLoading, label: name }),
  },

  improveReplyMatchStyleGuide: {
    id: 'improveReplyMatchStyleGuide',
    icon: 'widget-customize',
    name: 'Match to style guide',
    shortName: 'Style guide',
    commandName: 'richTextEditor:matchToStyleGuide',
    execution: {
      kind: 'writingImprovement',
      promptName: 'repeat-input',
      includeContext: true,
    },
    insert: 'replace',
    defaultPinned: false,
    gateDropdownOnStatus: true,
    loadingText: 'Matching to style guide',
    errorText: 'Failed to match to style guide',
    getStatus: ({ text, isLoading, channel, hasStyleGuideForChannel }) =>
      copilotUtils.matchToStyleGuide.getStatus({
        text,
        channel,
        hasStyleGuideForChannel,
        status: isLoading ? 'pending' : undefined,
      }),
  },

  translateToConversationLanguage: {
    id: 'translateToConversationLanguage',
    icon: 'translate-auto',
    name: 'Quick translate',
    shortName: 'Translate',
    commandName: 'richTextEditor:translateToConversationLanguage',
    execution: { kind: 'translateToConversationLanguage' },
    insert: 'replace',
    defaultPinned: true,
    gateDropdownOnStatus: true,
    loadingText: ({ languageName }) =>
      languageName ? `Translating to ${languageName}` : 'Translating',
    errorText: 'Failed to translate',
    getStatus: ({ text, isLoading, hasLanguage }) =>
      copilotUtils.quickTranslate.getStatus({
        text,
        hasLanguage: !!hasLanguage,
        isLoading,
      }),
  },

  translate: {
    id: 'translate',
    icon: 'translate',
    name: 'Custom translate',
    shortName: 'Custom translate',
    commandName: 'richTextEditor:translate',
    // The panel collects the target language, so a pinned click opens it.
    execution: { kind: 'delegateToMenu' },
    insert: 'replace',
    defaultPinned: false,
  },

  checkSpellingAndGrammar: {
    id: 'checkSpellingAndGrammar',
    icon: 'grammar-spelling',
    name: 'Fix spelling and grammar',
    shortName: 'Spelling',
    commandName: 'richTextEditor:checkSpellingAndGrammar',
    execution: { kind: 'spellingGrammar' },
    insert: 'replace',
    defaultPinned: false,
    gateDropdownOnStatus: true,
    loadingText: 'Checking spelling and grammar',
    errorText: 'Failed to check spelling and grammar',
    getStatus: ({ text, isLoading }) =>
      copilotUtils.spellingAndGrammar.getStatus({ text, isLoading }),
  },

  expandText: {
    id: 'expandText',
    icon: 'chat-plus',
    name: 'Expand reply',
    shortName: 'Expand',
    commandName: 'richTextEditor:expandText',
    execution: {
      kind: 'writingImprovement',
      promptName: 'expand_reply',
      includeContext: true,
    },
    insert: 'replace',
    defaultPinned: true,
    gateDropdownOnStatus: true,
    loadingText: 'Expanding text',
    errorText: 'Failed to expand',
    getStatus: ({ text, isLoading }) =>
      copilotUtils.expandText.getStatus({
        text,
        status: isLoading ? 'pending' : undefined,
      }),
  },

  quickAiDraft: {
    id: 'quickAiDraft',
    icon: 'mim',
    name: 'Quick Mim drafts',
    shortName: 'Quick draft',
    commandName: 'richTextEditor:quickAiDraft',
    execution: { kind: 'agentDraft' },
    insert: 'replace',
    defaultPinned: true,
    gateDropdownOnStatus: true,
    loadingText: 'Generating draft',
    errorText: 'Failed to generate draft',
    featureFlag: 'mimDrafts',
    getStatus: ({
      name,
      isLoading,
      hasDefaultDraftAgent,
      hasUnsupportedAttachment,
      csid,
    }) => ({
      isDisabled:
        !csid ||
        !hasDefaultDraftAgent ||
        !!hasUnsupportedAttachment ||
        !!isLoading,
      tooltipContent: hasUnsupportedAttachment
        ? unsupportedAttachmentReason
        : !hasDefaultDraftAgent
          ? 'No default AI agent set'
          : isLoading
            ? 'Generating draft...'
            : name,
    }),
  },

  customAiDraft: {
    id: 'customAiDraft',
    icon: 'mim',
    name: 'Custom Mim drafts',
    shortName: 'Custom draft',
    commandName: 'richTextEditor:customAiDraft',
    execution: { kind: 'delegateToMenu' },
    insert: 'replace',
    defaultPinned: false,
    gateDropdownOnStatus: true,
    featureFlag: 'mimDrafts',
    getStatus: ({ name, hasUnsupportedAttachment }) => ({
      isDisabled: !!hasUnsupportedAttachment,
      tooltipContent: hasUnsupportedAttachment
        ? unsupportedAttachmentReason
        : name,
    }),
  },
};

export const copilotActionIds = Object.keys(
  copilotActionRegistry,
) as CopilotActionId[];

/** Which actions start pinned, derived from the registry. */
export const defaultPinnedState = Object.fromEntries(
  copilotActionIds.map((id) => [id, copilotActionRegistry[id].defaultPinned]),
) as Record<CopilotActionId, boolean>;
